import { useCallback, useEffect, useRef, useState } from "react";
import { AlertOctagon, LogOut, X } from "lucide-react";

import { authApi, DEBTOR_BLOCK_EVENT } from "@/lib/api";

/**
 * Режим «должник»: аккаунт закрыт администратором платформы.
 *
 * Что делает: показывает несъезжаемую красную плашку сверху, а на любое
 * нажатие — красный экран с текстом, который написал администратор.
 * Прокрутка и чтение остаются: данные никуда не делись, и человек должен
 * увидеть, за что его закрыли, и по какому телефону это решается.
 *
 * Настоящий запрет живёт не здесь, а на сервере (subscription_write_guard
 * отбивает любую запись 403 с debtor_block). Этот компонент только
 * объясняет происходящее: кнопка, отключённая одним лишь фронтендом,
 * обходится консолью браузера за минуту.
 *
 * Клики перехватываются на фазе capture у document — до того, как их
 * увидит React (его слушатели висят на корне приложения) и до Radix,
 * который открывает поповеры по pointerdown. Иначе «заблокированное»
 * приложение продолжало бы открывать диалоги под красным экраном.
 */

const POLL_MS = 60_000;

/** Элементы самой плашки: внутри неё нажатия обязаны работать. */
const ALLOW_ATTR = "data-debtor-allow";

export default function DebtorBlockGate() {
    const [block, setBlock] = useState(null);   // {enabled, message} | null
    const [shown, setShown] = useState(false);  // открыт ли красный экран
    const blockRef = useRef(null);

    const apply = useCallback((next) => {
        blockRef.current = next?.enabled ? next : null;
        setBlock(blockRef.current);
        if (!next?.enabled) setShown(false);
    }, []);

    // Состояние спрашиваем у сервера, а не берём из localStorage: блокировку
    // включают посреди рабочего дня, и сохранённый при входе объект о ней
    // ничего не знает. Тот же запрос ловит и снятие блокировки.
    const refresh = useCallback(() => {
        if (!localStorage.getItem("tabel_token")) return;
        authApi.me()
            .then(({ data }) => apply(data?.debtor_block))
            .catch(() => {});
    }, [apply]);

    useEffect(() => {
        refresh();
        const timer = setInterval(() => {
            if (document.visibilityState !== "hidden") refresh();
        }, POLL_MS);
        window.addEventListener("focus", refresh);
        return () => {
            clearInterval(timer);
            window.removeEventListener("focus", refresh);
        };
    }, [refresh]);

    // Запись отбилась с 403 — значит блокировку включили только что, ждать
    // следующего опроса незачем.
    useEffect(() => {
        const onBlocked = (event) => {
            const message = event.detail?.message;
            apply({ enabled: true, message: message || blockRef.current?.message || "" });
            setShown(true);
        };
        window.addEventListener(DEBTOR_BLOCK_EVENT, onBlocked);
        return () => window.removeEventListener(DEBTOR_BLOCK_EVENT, onBlocked);
    }, [apply]);

    // Перехват нажатий. Вешаем, только пока блокировка включена, — чтобы у
    // всех остальных пользователей на каждый клик не висело лишних
    // обработчиков.
    useEffect(() => {
        if (!block) return undefined;

        const allowed = (target) =>
            target instanceof Element && !!target.closest(`[${ALLOW_ATTR}]`);

        const intercept = (event) => {
            if (allowed(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
            if (typeof event.stopImmediatePropagation === "function") {
                event.stopImmediatePropagation();
            }
            setShown(true);
        };

        // pointerdown — чтобы Radix не успел открыть меню; click — чтобы не
        // ушла навигация по ссылке; submit — чтобы не отправилась форма по
        // Enter; keydown — Enter и пробел на кнопке в фокусе.
        const onKeyDown = (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            const tag = event.target?.tagName;
            if (tag === "TEXTAREA") return;     // перенос строки ничего не меняет
            if (event.key === " " && (tag === "INPUT" || tag === "TEXTAREA")) return;
            intercept(event);
        };

        document.addEventListener("pointerdown", intercept, true);
        document.addEventListener("click", intercept, true);
        document.addEventListener("submit", intercept, true);
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("pointerdown", intercept, true);
            document.removeEventListener("click", intercept, true);
            document.removeEventListener("submit", intercept, true);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [block]);

    // Плашка висит fixed поверх всего, а оболочка приложения — во всю высоту
    // экрана (.app-shell h-screen). Без отступа плашка накрыла бы шапку, а
    // добавить его можно только снаружи: разметка Layout — flex-строка, и
    // распорка внутри неё стала бы колонкой шириной 38px.
    useEffect(() => {
        document.body.classList.toggle("debtor-blocked", !!block);
        return () => document.body.classList.remove("debtor-blocked");
    }, [block]);

    if (!block) return null;

    const logout = () => {
        localStorage.removeItem("tabel_token");
        localStorage.removeItem("tabel_user");
        window.location.href = "/login";
    };

    return (
        <>
            {/* Постоянная плашка: причина должна быть видна и без нажатий. */}
            <div
                {...{ [ALLOW_ATTR]: "" }}
                className="fixed top-0 left-0 right-0 z-[9998] px-4 py-2 text-center text-sm font-semibold text-white"
                style={{ background: "#dc2626", boxShadow: "0 2px 12px rgba(220,38,38,0.45)" }}
                role="status"
            >
                <AlertOctagon className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                Доступ приостановлен администратором. Изменения невозможны — данные открыты только для просмотра.
            </div>

            {shown && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                    style={{ background: "rgba(69,10,10,0.72)", backdropFilter: "blur(6px)" }}
                    role="alertdialog"
                    aria-modal="true"
                >
                    <div
                        {...{ [ALLOW_ATTR]: "" }}
                        className="w-full max-w-lg rounded-2xl p-6 text-white shadow-2xl"
                        style={{
                            background: "linear-gradient(180deg, #dc2626 0%, #b91c1c 100%)",
                            border: "1px solid rgba(255,255,255,0.25)",
                        }}
                    >
                        <div className="flex items-start gap-3">
                            <AlertOctagon className="w-8 h-8 shrink-0" />
                            <div className="min-w-0">
                                <div className="text-lg font-bold">Доступ приостановлен</div>
                                <div className="text-sm opacity-90">
                                    Аккаунт закрыт администратором платформы.
                                </div>
                            </div>
                            <button
                                onClick={() => setShown(false)}
                                className="ml-auto p-1 rounded-lg hover:bg-white/15 transition-colors"
                                title="Закрыть"
                                aria-label="Закрыть"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Текст администратора — главное на этом экране.
                            whitespace-pre-line: админ пишет телефон и часы
                            работы отдельными строками, и переносы должны
                            дожить до экрана. */}
                        <div
                            className="mt-4 rounded-xl px-4 py-3 text-base font-semibold whitespace-pre-line break-words"
                            style={{ background: "rgba(0,0,0,0.22)" }}
                        >
                            {block.message}
                        </div>

                        <div className="mt-4 text-xs opacity-90">
                            Данные не удалены и никуда не денутся — их по-прежнему можно
                            открыть и просмотреть. Работа возобновится сразу после того,
                            как администратор снимет ограничение.
                        </div>

                        <div className="mt-5 flex flex-wrap gap-2 justify-end">
                            <button
                                onClick={logout}
                                className="px-4 py-2 rounded-xl text-sm font-semibold bg-white/15 hover:bg-white/25 transition-colors inline-flex items-center gap-1.5"
                            >
                                <LogOut className="w-4 h-4" /> Выйти
                            </button>
                            <button
                                onClick={() => setShown(false)}
                                className="px-4 py-2 rounded-xl text-sm font-bold transition-colors"
                                style={{ background: "#ffffff", color: "#b91c1c" }}
                            >
                                Понятно
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
